// -----------------------------------------------------------------------------
// ChecklistMiniGrid
//
// Shared mini-card grid for the Treatment Started hub (CompletionChecklist),
// the Follow-up V2 section (ScheduleReminderTaskSection) and both attended
// checklists (AttendedGuidedChecklist + V2).
//
// Readability rules:
//   - 3 cards per row while collapsed, 4 per row once expanded ("Show N more").
//   - Collapsed shows the first COLLAPSED_LIMIT (6 = 2 rows of 3) cards.
//   - Cards are grouped under small "<template name> · <count>" headings
//     (order preserved: due-date order, group order = first appearance).
//   - Titles WRAP (never ellipsis-truncated).
//   - The date chip only renders when it adds information: hidden when it is
//     the same calendar day as the featured card's due date, always kept for
//     overdue items or a differing date.
//   - The featured item (featuredItem) is excluded from the grid — it already
//     has its own hero card.
//
// Also renders done items (completed / skipped / paused) as outcome chips with
// an Undo button (Task #15).
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import dayjs from 'dayjs';
import { Box, Typography, IconButton, Menu, MenuItem, ListItemIcon } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { parseApiDate } from 'src/utils/taskDateUtils';
import { ACTION_META, DEFAULT_ACTION_META } from './actionMeta';

// Outcome chip styles per completion state
const COMPLETION_CHIP = {
    YES:     { label: 'Done',    bg: 'rgba(52,199,89,0.10)',   color: '#34c759' },
    SKIPPED: { label: 'Skipped', bg: 'rgba(110,110,115,0.10)', color: '#6e6e73' },
    PAUSED:  { label: 'Paused',  bg: 'rgba(255,171,0,0.10)',   color: '#FFAB00' },
};

// Suffix appended to a SKIPPED chip when the backend auto-skipped the item
// because the lead lacks the contact detail it needs (response_payload.auto_skipped).
const AUTO_SKIP_SUFFIX = {
    no_email: ' — no email',
    no_phone: ' — no phone',
};

// Collapsed: 2 rows of 3. Expanded: everything, 4 per row.
export const COLLAPSED_LIMIT = 6;
export const DONE_COLLAPSED_LIMIT = 6;
export const COLS_COLLAPSED = 3;
export const COLS_EXPANDED = 4;

function formatDueShort(dueDateStr) {
    if (!dueDateStr) return null;
    const now = dayjs();
    const due = parseApiDate(dueDateStr);          // API date — DISPLAY/COMPARE
    if (!due || !due.isValid()) return null;
    // Day-based overdue: only when due on a PRIOR calendar day. A task due any
    // time today reads "Today HH:mm", never overdue (matches eyebrow + strip).
    if (due.isBefore(now.startOf('day'))) {
        const days = now.diff(due, 'days');
        if (days >= 1) return `Overdue ${days}d`;
        return `Overdue ${Math.max(now.diff(due, 'hours'), 1)}h`;
    }
    if (due.isSame(now, 'day')) return `Today ${due.format('HH:mm')}`;
    return due.format('D MMM');
}

// Default datetime-local string for the change-due-date picker: tomorrow 09:00.
function defaultDueLocal() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
}

export function MiniCard({ item, onClick, onSkip, onChangeDueDate, onMarkComplete, featuredDueAt }) {
    const meta = ACTION_META[item.item_type] || DEFAULT_ACTION_META;
    // COMPARE — API date vs now (parseApiDate handles both DRF + ISO).
    // Day-based: overdue only when due on a PRIOR calendar day (matches eyebrow + strip).
    const isOverdue = !!(item.due_at && parseApiDate(item.due_at)?.isBefore(dayjs().startOf('day')));
    const title = item.title || item.name || meta.label;
    // Date chip only when it adds info: overdue, or a different day from the
    // featured card's due date (which already shows it).
    const sameDayAsFeatured = !!(featuredDueAt && item.due_at
        && parseApiDate(item.due_at)?.isSame(parseApiDate(featuredDueAt), 'day'));
    const dueText = (isOverdue || !sameDayAsFeatured) ? formatDueShort(item.due_at) : null;
    const chipBg = isOverdue ? 'rgba(255,86,48,0.10)' : 'rgba(255,171,0,0.10)';
    const chipColor = isOverdue ? '#FF5630' : '#FFAB00';

    // Overflow (⋯) menu is ADDITIVE: it only renders when at least one of the
    // per-item Skip / Change-due-date / Mark-as-completed handlers is supplied.
    // The attended popup passes none → this whole block is inert, card renders as before.
    // NOTE: Pause is intentionally NOT a per-item action here — the /pause/
    // endpoint pauses the whole task-group, so the hub surfaces it as a single
    // checklist-level "Pause checklist" control instead.
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [dueAnchor, setDueAnchor] = useState(null);
    const [dueValue, setDueValue] = useState('');
    // Wave A Change 3: LEAD_NEXT_ACTION requires resolved_action — hide direct complete.
    const requiresActionFirst = item.item_type === 'LEAD_NEXT_ACTION';
    const hasOverflow = !!(onSkip || onChangeDueDate || (onMarkComplete && !requiresActionFirst));

    const openMenu = (e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); };
    const closeMenu = () => setMenuAnchor(null);

    const handleSkipClick = (e) => { e.stopPropagation(); closeMenu(); onSkip?.(item); };
    const handleMarkComplete = (e) => { e.stopPropagation(); closeMenu(); onMarkComplete?.(item); };
    const handleDueOpen = (e) => {
        e.stopPropagation();
        // DISPLAY — API date → datetime-local string for the picker.
        setDueValue(item.due_at ? (parseApiDate(item.due_at)?.format('YYYY-MM-DDTHH:mm') || defaultDueLocal()) : defaultDueLocal());
        setDueAnchor(e.currentTarget);
        closeMenu();
    };
    const handleDueConfirm = (e) => {
        e.stopPropagation();
        if (dueValue && onChangeDueDate) onChangeDueDate(item, new Date(dueValue));
        setDueAnchor(null);
    };

    return (
        <Box
            data-testid={`mini-card-${item.id ?? item.checklist_item_id}`}
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
            sx={{
                flex: '1 1 0',
                minWidth: 0,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                px: 1.25,
                py: 1,
                bgcolor: '#ffffff',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'background 150ms, box-shadow 160ms, transform 160ms',
                '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.02)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    transform: 'translateY(-1px)',
                },
            }}
        >
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '9px',
                    bgcolor: `${meta.color}1A`,
                    border: `1px solid ${meta.color}26`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <Iconify icon={meta.icon} width={16} height={16} sx={{ color: meta.color }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                    data-testid="mini-card-title"
                    title={title}
                    sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#1d1d1f',
                        letterSpacing: '-0.005em',
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        lineHeight: 1.3,
                    }}
                >
                    {title}
                </Typography>
                {dueText && (
                    <Typography
                        sx={{
                            display: 'inline-block',
                            mt: 0.25,
                            px: 0.75,
                            py: 0.1,
                            borderRadius: '980px',
                            bgcolor: chipBg,
                            color: chipColor,
                            fontSize: '10px',
                            fontWeight: 500,
                            letterSpacing: '-0.005em',
                        }}
                    >
                        {dueText}
                    </Typography>
                )}
            </Box>

            {hasOverflow && (
                <>
                    <IconButton
                        data-testid={`mini-card-overflow-${item.id ?? item.checklist_item_id}`}
                        size="small"
                        aria-label="More actions"
                        onClick={openMenu}
                        sx={{ p: 0.25, ml: 0.5, flexShrink: 0, color: '#6e6e73', '&:hover': { color: '#1d1d1f', bgcolor: 'rgba(0,0,0,0.04)' } }}
                    >
                        <Iconify icon="eva:more-horizontal-fill" width={16} height={16} />
                    </IconButton>
                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={(e) => { e?.stopPropagation?.(); closeMenu(); }}
                        onClick={(e) => e.stopPropagation()}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        {/* Change 1: Mark as completed — wired to existing handlePanelComplete path.
                            Wave A Change 3: hidden for LEAD_NEXT_ACTION. */}
                        {onMarkComplete && !requiresActionFirst && (
                            <MenuItem data-testid="mini-card-mark-complete" onClick={handleMarkComplete} sx={{ fontSize: '13px', color: '#34c759' }}>
                                <ListItemIcon sx={{ minWidth: 28 }}><Iconify icon="mdi:check-circle-outline" width={16} height={16} sx={{ color: '#34c759' }} /></ListItemIcon>
                                Mark as completed
                            </MenuItem>
                        )}
                        {onSkip && (
                            <MenuItem data-testid="mini-card-skip" onClick={handleSkipClick} sx={{ fontSize: '13px' }}>
                                <ListItemIcon sx={{ minWidth: 28 }}><Iconify icon="eva:skip-forward-outline" width={16} height={16} /></ListItemIcon>
                                Skip
                            </MenuItem>
                        )}
                        {onChangeDueDate && (
                            <MenuItem data-testid="mini-card-change-due" onClick={handleDueOpen} sx={{ fontSize: '13px' }}>
                                <ListItemIcon sx={{ minWidth: 28 }}><Iconify icon="eva:calendar-outline" width={16} height={16} /></ListItemIcon>
                                Change due date
                            </MenuItem>
                        )}
                    </Menu>

                    {/* Change-due-date popover — datetime-local + confirm */}
                    <Menu
                        anchorEl={dueAnchor}
                        open={Boolean(dueAnchor)}
                        onClose={(e) => { e?.stopPropagation?.(); setDueAnchor(null); }}
                        onClick={(e) => e.stopPropagation()}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{ sx: { p: 1.5, borderRadius: '12px' } }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 220 }} onClick={(e) => e.stopPropagation()}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43' }}>New due date</Typography>
                            <input
                                data-testid="mini-card-due-input"
                                type="datetime-local"
                                value={dueValue}
                                onChange={(e) => setDueValue(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 10px',
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    border: '1.5px solid rgba(0,0,0,0.15)',
                                    fontFamily: 'inherit',
                                    color: '#1d1d1f',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                }}
                            />
                            <Box
                                component="button"
                                data-testid="mini-card-due-confirm"
                                disabled={!dueValue}
                                onClick={handleDueConfirm}
                                sx={{
                                    border: 'none',
                                    borderRadius: '8px',
                                    bgcolor: '#078DEE',
                                    color: '#fff',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    py: 0.75,
                                    cursor: 'pointer',
                                    fontFamily: 'inherit',
                                    '&:disabled': { bgcolor: 'rgba(7,141,238,0.4)', cursor: 'default' },
                                }}
                            >
                                Update due date
                            </Box>
                        </Box>
                    </Menu>
                </>
            )}
        </Box>
    );
}
MiniCard.propTypes = {
    item: PropTypes.object.isRequired,
    onClick: PropTypes.func,
    /** Additive — when supplied, the overflow (⋯) menu exposes Skip. */
    onSkip: PropTypes.func,
    /** Additive — when supplied, the overflow (⋯) menu exposes Change due date. */
    onChangeDueDate: PropTypes.func,
    /** Change 1: additive — when supplied, the overflow (⋯) menu exposes Mark as completed. */
    onMarkComplete: PropTypes.func,
    /** Featured card's due_at — the date chip is hidden when it is the same day. */
    featuredDueAt: PropTypes.string,
};
MiniCard.defaultProps = { onClick: null, onSkip: null, onChangeDueDate: null, onMarkComplete: null, featuredDueAt: null };

// ---------------------------------------------------------------------------
// DoneCard — completed / skipped / paused item with outcome chip + Undo
// ---------------------------------------------------------------------------

export function DoneCard({ item, onUndo }) {
    const meta = ACTION_META[item.item_type] || DEFAULT_ACTION_META;
    const title = item.title || item.name || meta.label;
    // Resolve completion state: prefer the item's own field; if absent but
    // paused_until is set, infer PAUSED (server may omit completion_state).
    // Fall back to 'YES' only for genuinely done items with no explicit state.
    const rawState = item.completion_state || (item.paused_until ? 'PAUSED' : null);
    const state = (rawState && COMPLETION_CHIP[rawState]) ? rawState : 'YES';
    const chip = COMPLETION_CHIP[state];

    // For PAUSED items, append the date the item is paused until (if available).
    // paused_until comes back as the app's "DD-MM-YYYY HH:mm:ss.SSSSSS[Z]" format
    // (e.g. "04-06-2026 10:00:00.000000Z" = 4 June) OR ISO. parseApiDate
    // auto-detects both; a bare moment(str) would mis-parse the DRF form as M-D-Y.
    const pausedUntil = state === 'PAUSED' ? (item.paused_until || null) : null;
    const pausedDate = pausedUntil ? parseApiDate(pausedUntil) : null;     // DISPLAY
    const pausedUntilLabel = pausedDate && pausedDate.isValid()
        ? ` until ${pausedDate.format('D MMM')}`
        : '';
    // Auto-skipped (no contact detail on file) → name the reason so the operator
    // can see WHY it skipped rather than a bare "Skipped". Backend sets
    // response_payload.auto_skipped = 'no_email' | 'no_phone'.
    const autoSkipped = state === 'SKIPPED' ? item.response_payload?.auto_skipped : null;
    const autoSkipSuffix = AUTO_SKIP_SUFFIX[autoSkipped] || '';
    const chipLabel = `${chip.label}${pausedUntilLabel}${autoSkipSuffix}`;

    return (
        <Box
            data-testid={`done-card-${item.id ?? item.checklist_item_id}`}
            sx={{
                flex: '1 1 0',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 1,
                bgcolor: '#fafafa',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: '10px',
                opacity: 0.8,
            }}
        >
            <Box
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    bgcolor: `${meta.color}0A`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <Iconify icon={meta.icon} width={14} height={14} sx={{ color: meta.color, opacity: 0.5 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                    data-testid="done-card-title"
                    title={title}
                    sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#6e6e73',
                        letterSpacing: '-0.005em',
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        lineHeight: 1.3,
                        textDecoration: 'line-through',
                    }}
                >
                    {title}
                </Typography>
                <Box
                    component="span"
                    sx={{
                        display: 'inline-block',
                        mt: 0.25,
                        px: 0.75,
                        py: 0.1,
                        borderRadius: '980px',
                        bgcolor: chip.bg,
                        color: chip.color,
                        fontSize: '10px',
                        fontWeight: 600,
                        letterSpacing: '-0.005em',
                    }}
                >
                    {chipLabel}
                </Box>
            </Box>
            {onUndo && (
                <Box
                    component="button"
                    data-testid={`undo-btn-${item.id ?? item.checklist_item_id}`}
                    onClick={(e) => { e.stopPropagation(); onUndo(item); }}
                    sx={{
                        background: 'none',
                        border: '1px solid rgba(0,0,0,0.12)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        px: 0.875,
                        py: 0.3,
                        fontSize: '11px',
                        fontWeight: 500,
                        color: '#6e6e73',
                        letterSpacing: '-0.005em',
                        fontFamily: 'inherit',
                        flexShrink: 0,
                        transition: 'background 150ms, color 150ms',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: '#1d1d1f' },
                    }}
                >
                    Undo
                </Box>
            )}
        </Box>
    );
}
DoneCard.propTypes = {
    item: PropTypes.object.isRequired,
    onUndo: PropTypes.func,
};
DoneCard.defaultProps = { onUndo: null };

function MoreButton({ count, onClick, expanded }) {
    return (
        <Box
            data-testid="checklist-mini-more-btn"
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
            sx={{
                flex: '0 0 auto',
                gridColumn: '1 / -1',
                justifySelf: 'start',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                px: 1.5,
                py: 1,
                bgcolor: 'rgba(7,141,238,0.06)',
                border: '1px solid rgba(7,141,238,0.20)',
                borderRadius: '10px',
                cursor: 'pointer',
                color: '#078DEE',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '-0.005em',
                whiteSpace: 'nowrap',
                transition: 'background 150ms',
                '&:hover': { bgcolor: 'rgba(7,141,238,0.10)' },
            }}
        >
            <Iconify
                icon={expanded ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
                width={14}
                height={14}
            />
            {expanded ? 'Show less' : `Show ${count} more`}
        </Box>
    );
}
MoreButton.propTypes = {
    count: PropTypes.number.isRequired,
    onClick: PropTypes.func.isRequired,
    expanded: PropTypes.bool,
};
MoreButton.defaultProps = { expanded: false };

// Columns: 3 collapsed / 4 expanded on wide screens; 1 column on phones and
// 2 on tablets (<900px) so cards stay readable at <=768px.
const gridSx = (cols) => ({
    display: 'grid',
    width: '100%',
    gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, minmax(0, 1fr))',
        md: `repeat(${cols}, minmax(0, 1fr))`,
    },
    gap: 1,
    alignItems: 'stretch',
});

const GROUP_HEADING_SX = {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6e6e73',
    letterSpacing: '0.02em',
    mt: 1.25,
    mb: 0.5,
};

// Group cards by checklist/template, preserving the incoming order (group order
// = first appearance). Returns [{ key, name, items }]. When no item carries a
// template name there is nothing to head, so a single unnamed group is returned.
export function groupByTemplate(cards) {
    const map = new Map();
    cards.forEach((item) => {
        const name = item.templateName || item._templateName || '';
        const key = item.templateId != null ? `t:${item.templateId}` : `n:${name}`;
        if (!map.has(key)) map.set(key, { key, name, items: [] });
        map.get(key).items.push(item);
    });
    return [...map.values()];
}

const itemKey = (item) => item.id ?? item.checklist_item_id ?? item.sort_order;

export default function ChecklistMiniGrid({ groups, onItemClick, onUndo, onSkip, onChangeDueDate, onMarkComplete, featuredItem }) {
    const [expanded, setExpanded] = useState(false);
    const [doneExpanded, setDoneExpanded] = useState(false);
    const { overdue = [], today = [], later = [], done = [] } = groups || {};
    // Upcoming cards must always read in due-date order (earliest first) and
    // must re-order live after a reschedule. today items (≤ end-of-today) sort
    // ahead of later items (future); undated items (due_at null → Infinity) last.
    const byDueAsc = (a, b) =>                                              // COMPARE/SORT
        (a.due_at ? (parseApiDate(a.due_at)?.valueOf() ?? Infinity) : Infinity) -
        (b.due_at ? (parseApiDate(b.due_at)?.valueOf() ?? Infinity) : Infinity);
    const featuredId = featuredItem?.id ?? featuredItem?.checklist_item_id ?? null;
    // The featured item has its own hero card — never repeat it as a mini-card.
    const active = [...overdue, ...today, ...later]
        .filter((i) => featuredId == null || itemKey(i) !== featuredId)
        .sort(byDueAsc);
    if (active.length === 0 && done.length === 0) return null;

    const visible = expanded ? active : active.slice(0, COLLAPSED_LIMIT);
    const hiddenCount = active.length - COLLAPSED_LIMIT;

    const visibleDone = doneExpanded ? done : done.slice(0, DONE_COLLAPSED_LIMIT);
    const hiddenDoneCount = done.length - DONE_COLLAPSED_LIMIT;

    const activeCols = expanded ? COLS_EXPANDED : COLS_COLLAPSED;
    const doneCols = doneExpanded ? COLS_EXPANDED : COLS_COLLAPSED;
    const templateGroups = groupByTemplate(visible);
    // Totals per group (all active, not just visible) so the heading count is true.
    const totals = new Map(groupByTemplate(active).map((g) => [g.key, g.items.length]));
    const showHeadings = templateGroups.some((g) => g.name);
    const featuredDueAt = featuredItem?.due_at || null;

    return (
        <Box data-testid="checklist-mini-grid">
            {/* Active items — grouped by checklist, responsive 3 / 4 column grid */}
            {active.length > 0 && (
                <Box data-testid="mini-grid-active" data-cols={activeCols}>
                    {templateGroups.map((g) => (
                        <Box key={g.key} data-testid="mini-grid-group">
                            {showHeadings && g.name && (
                                <Typography data-testid="mini-grid-group-heading" sx={GROUP_HEADING_SX}>
                                    {`${g.name} · ${totals.get(g.key)}`}
                                </Typography>
                            )}
                            <Box data-testid="mini-grid-cards" sx={gridSx(activeCols)}>
                                {g.items.map((item) => (
                                    <MiniCard
                                        key={itemKey(item)}
                                        item={item}
                                        featuredDueAt={featuredDueAt}
                                        onClick={() => onItemClick?.(item)}
                                        onSkip={onSkip}
                                        onChangeDueDate={onChangeDueDate}
                                        onMarkComplete={onMarkComplete}
                                    />
                                ))}
                            </Box>
                        </Box>
                    ))}
                    {hiddenCount > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <MoreButton
                                count={hiddenCount}
                                expanded={expanded}
                                onClick={() => setExpanded((v) => !v)}
                            />
                        </Box>
                    )}
                </Box>
            )}

            {/* Done items — outcome chips with Undo */}
            {done.length > 0 && (
                <Box sx={{ mt: active.length > 0 ? 1.25 : 0 }} data-testid="mini-grid-done" data-cols={doneCols}>
                    <Box sx={gridSx(doneCols)}>
                        {visibleDone.map((item) => (
                            <DoneCard
                                key={itemKey(item)}
                                item={item}
                                onUndo={onUndo}
                            />
                        ))}
                    </Box>
                    {hiddenDoneCount > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <MoreButton
                                count={hiddenDoneCount}
                                expanded={doneExpanded}
                                onClick={() => setDoneExpanded((v) => !v)}
                            />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}

ChecklistMiniGrid.propTypes = {
    groups: PropTypes.shape({
        today: PropTypes.array,
        later: PropTypes.array,
        done: PropTypes.array,
    }),
    onItemClick: PropTypes.func,
    /** Called with the done item when user clicks Undo → reopens the checklist item. */
    onUndo: PropTypes.func,
    /** Additive — per-item Skip (overflow menu). Attended popup passes none. */
    onSkip: PropTypes.func,
    /** Additive — per-item Change due date (overflow menu). */
    onChangeDueDate: PropTypes.func,
    /** Change 1: additive — per-item Mark as completed (overflow menu). */
    onMarkComplete: PropTypes.func,
    /** The hero/featured item: excluded from the grid and its due date drives the date-chip rule. */
    featuredItem: PropTypes.object,
};

ChecklistMiniGrid.defaultProps = {
    groups: { today: [], later: [], done: [] },
    onItemClick: null,
    onUndo: null,
    onSkip: null,
    onChangeDueDate: null,
    onMarkComplete: null,
    featuredItem: null,
};
